import {ReactNode, useState} from 'react';
import {Box, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {AffiliatedUser} from '../../../../../../backend/responses/affiliated-user';
import {useSnackBar} from '../../../../../../contexts/snack-bar/context';
import {ReminderForm} from '../../../../../../components/reminder-form-dialog/reminder-form-dialog';
import {useModalBtns} from '../../../../../../components/modal/use-modal-btns';
import {translations} from '../../../../../../i18n/translations';
import {EntityUserRemindersRepository} from '../../../../../../repositories/entity-user-reminders';
import {
  buildReminderPayload,
  ReminderFormValues,
} from '../../../informal-caregiver/reminder/reminder-form.utils';
import {PlannerBaseModal} from '../../planner/calendar/modals/planner-base-modal';
import {PlannerActivityUsers} from '../../planner/calendar/modals/create/activity/3-planner-activity-users';

interface BulkRemindersModalProps {
  entityId: string;
  currentUserId: string;
  onClose: (changed: boolean) => void;
}

export function BulkRemindersModal({entityId, currentUserId, onClose}: BulkRemindersModalProps) {
  const {t} = useTranslation();
  const snackBar = useSnackBar();
  const [step, setStep] = useState<'details' | 'seniors'>('details');
  const [seniors, setSeniors] = useState<AffiliatedUser[]>([]);
  const [successfulIds, setSuccessfulIds] = useState<string[]>([]);
  const [submittedValues, setSubmittedValues] = useState<ReminderFormValues>();
  const [isSaving, setIsSaving] = useState(false);
  const [attempted, setAttempted] = useState(false);

  const createReminders = async (values: ReminderFormValues) => {
    if (!seniors.length || isSaving) return;
    setSubmittedValues(values);
    setIsSaving(true);
    const pending = seniors.filter((senior) => !successfulIds.includes(senior.user.id!));
    let succeeded = successfulIds.length;
    for (const senior of pending) {
      const seniorId = senior.user.id!;
      try {
        await new EntityUserRemindersRepository(entityId, seniorId).create(
          buildReminderPayload(values, currentUserId, seniorId)
        );
        succeeded += 1;
        setSuccessfulIds((current) => [...current, seniorId]);
      } catch {
        // Continue with the remaining seniors; failed requests can be retried.
      }
    }
    setAttempted(true);
    setIsSaving(false);
    const complete = succeeded === seniors.length;
    void snackBar.setOfficeToastNotification({
      severity: complete ? 'success' : 'error',
      message: t(
        complete ? translations.reminders.bulk_success : translations.reminders.bulk_partial,
        {
          success: succeeded,
          total: seniors.length,
        }
      ),
    });
    if (complete) onClose(true);
  };

  return (
    <ReminderForm
      isSubmitting={isSaving}
      onSave={async (values) => {
        if (step === 'details') setStep('seniors');
        else await createReminders(values);
      }}
    >
      {(form, submit) => (
        <BulkRemindersModalContent
          step={step}
          form={form}
          seniors={seniors}
          isSaving={isSaving}
          attempted={attempted}
          successfulCount={successfulIds.length}
          onClose={() => onClose(successfulIds.length > 0)}
          onBack={() => setStep('details')}
          onChangeSeniors={(users) =>
            setSeniors(
              users.filter((user) => user.user.isActive && user.affiliation.entityId === entityId)
            )
          }
          onSubmit={() => {
            if (attempted && submittedValues) void createReminders(submittedValues);
            else {
              void submit().then((valid) => {
                if (!valid && step === 'seniors') setStep('details');
              });
            }
          }}
        />
      )}
    </ReminderForm>
  );
}

interface BulkRemindersModalContentProps {
  step: 'details' | 'seniors';
  form: ReactNode;
  seniors: AffiliatedUser[];
  isSaving: boolean;
  attempted: boolean;
  successfulCount: number;
  onClose: () => void;
  onBack: () => void;
  onChangeSeniors: (users: AffiliatedUser[]) => void;
  onSubmit: () => void;
}

function BulkRemindersModalContent(props: BulkRemindersModalContentProps) {
  const {t} = useTranslation();
  const completed = props.attempted && props.successfulCount === props.seniors.length;
  const actions = useModalBtns({
    left: {
      type: props.step === 'details' || props.attempted ? 'cancel' : 'back',
      disabled: props.isSaving,
      onClick: props.step === 'details' || props.attempted ? props.onClose : props.onBack,
    },
    right: completed
      ? undefined
      : {
          type: props.step === 'details' ? 'next' : 'save',
          disabled: props.isSaving || (props.step === 'seniors' && !props.seniors.length),
          loading: props.isSaving,
          onClick: props.onSubmit,
        },
  });

  if (completed) actions[0].label = t(translations.family_profile.close);
  else if (props.step === 'seniors') {
    actions[1].label = t(
      props.attempted ? translations.reminders.retry : translations.reminders.bulk_title
    );
  }

  return (
    <PlannerBaseModal
      open
      onClose={props.isSaving ? undefined : props.onClose}
      title={t(translations.reminders.bulk_title)}
      actions={actions}
    >
      <Stack spacing={2}>
        <Box sx={{display: props.step === 'details' ? 'block' : 'none'}}>{props.form}</Box>
        {props.step === 'seniors' && (
          <PlannerActivityUsers
            users={props.seniors}
            disabled={props.isSaving || props.attempted}
            onChange={props.onChangeSeniors}
          />
        )}
      </Stack>
    </PlannerBaseModal>
  );
}
